import React, { useEffect, useMemo, useState,useRef } from "react";
import "./Timer.css";
import useAppHooks from "../../hooks/useAppHooks";
import { setTimeLeft ,updateQuestionTime } from "../../../redux/exam/examSlice";

const Timer = ({ onTimeUp }) => {
  const { dispatch, selector } = useAppHooks();
const testDetails = selector((state) => state.examSlice.testDetails);
const isPracticeMode =
  testDetails?.practiceMode === "practice";

const currentTestId =
  testDetails?.testId ||
  testDetails?.setId ||
  testDetails?.paperId;

const testKey = `exam_timeLeft_${currentTestId}`;
  const rawDuration = selector((state) => state.examSlice.paper_duration);
const paperDuration = isPracticeMode ? 0 : (rawDuration || 10800);
const questionsList = selector((state) => state.examSlice.questionsList);
const timeStatus = questionsList?.time_status || {};

  // Read time from redux
  const timeLeft = selector((state) => state?.examSlice?.timeLeft);
   const submitResult = selector((state) => state.examSlice.submitResult);
const currentQuestionIndex = selector((state) => state.examSlice.currentQuestionIndex);
const questionsData = selector((state) => state.examSlice.questionsData);
  const [toast, setToast] = useState(null);
  const shownToastsRef = useRef(new Set());
  const prevMetricRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const hasTriggeredTimeUpRef = useRef(false);

  const parseMinutesFromText = (text) => {
    const match = String(text || "").match(/(\d+)\s*min/i);
    return match ? Number(match[1]) * 60 : null;
  };

useEffect(() => {
  if (isPracticeMode && timeLeft === null) {
    dispatch(setTimeLeft(0));
  }
}, [isPracticeMode, timeLeft, dispatch]);
useEffect(() => {
  if (submitResult) return;

  if (timeLeft === null || timeLeft === undefined) return;

  const interval = setInterval(() => {
    let next;

    if (isPracticeMode) {
      // Practice → count up from 0
      next = timeLeft + 1;
      dispatch(setTimeLeft(next));
      localStorage.setItem(testKey, String(next));
    } else {
      // Test → countdown
      if (timeLeft <= 0) return;

      next = timeLeft - 1;

      if (next <= 0) {
        dispatch(setTimeLeft(0));
        localStorage.removeItem(testKey);
      } else {
        dispatch(setTimeLeft(next));
        localStorage.setItem(testKey, String(next));
      }
    }

    dispatch(updateQuestionTime({ index: currentQuestionIndex }));
  }, 1000);

  return () => clearInterval(interval);
}, [
  timeLeft,
  submitResult,
  currentQuestionIndex,
  dispatch,
  isPracticeMode,
  testKey,
]);

  // Clear shown toast cache on new test/practice switch
  useEffect(() => {
    shownToastsRef.current.clear();
    prevMetricRef.current = null;
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }, [currentTestId, isPracticeMode]);

  // Toast trigger logic (test mode only, based on elapsed time)
  useEffect(() => {
    if (isPracticeMode) return;
    if (timeLeft === null || timeLeft === undefined) return;

    const firstTime = Number(timeStatus.first?.time ?? 0);
    const secondTime = Number(timeStatus.second?.time ?? 0);
    const thirdTime = Number(timeStatus.third?.time ?? 0);
    const secondToastTime = Number(timeStatus.second?.toast_time ?? 0);
    const thirdToastTime = Number(timeStatus.third?.toast_time ?? 0);

    // Some payloads use elapsed thresholds, some behave like remaining thresholds.
    const hasOutOfRangeThreshold = [firstTime, secondTime, thirdTime, secondToastTime, thirdToastTime]
      .some((t) => Number.isFinite(t) && t > paperDuration + 5);
    const useElapsedMode = !hasOutOfRangeThreshold;

    const elapsed = Math.max(paperDuration - timeLeft, 0);
    const currentMetric = useElapsedMode ? elapsed : timeLeft;

    if (prevMetricRef.current === null) {
      prevMetricRef.current = currentMetric;
      return;
    }
    const previousMetric = prevMetricRef.current;

    const resolveToastThreshold = (status) => {
      const rawToast = Number(status?.toast_time ?? 0);
      if (useElapsedMode) return rawToast || null;

      if (rawToast > 0 && rawToast <= paperDuration + 5) {
        return rawToast;
      }
      const fromText = parseMinutesFromText(status?.toast_text);
      if (fromText) return fromText;

      const fromTime = Number(status?.time ?? 0);
      return fromTime > 0 ? Math.min(fromTime, paperDuration) : null;
    };

    const checkToast = (status, key) => {
      const toastTime = resolveToastThreshold(status);
      if (!toastTime || toastTime <= 0) return;
      if (shownToastsRef.current.has(key)) return;

      const crossed = useElapsedMode
        ? previousMetric < toastTime && currentMetric >= toastTime
        : previousMetric > toastTime && currentMetric <= toastTime;

      if (crossed) {
        shownToastsRef.current.add(key);
        setToast({
          text: status.toast_text,
          backgroundColor: status.toast_bg_color || "rgba(0, 0, 0, 0.8)",
          color: status.toast_text_color || "#FFF",
        });

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }
        toastTimeoutRef.current = setTimeout(() => setToast(null), 5000);
      }
    };

    checkToast(timeStatus.second, "second");
    checkToast(timeStatus.third, "third");
    prevMetricRef.current = currentMetric;
  }, [isPracticeMode, paperDuration, timeLeft, timeStatus]);

  useEffect(() => {
    if (isPracticeMode) return;

    if (timeLeft > 0) {
      hasTriggeredTimeUpRef.current = false;
      return;
    }

    if (timeLeft === 0 && !hasTriggeredTimeUpRef.current) {
      hasTriggeredTimeUpRef.current = true;
      onTimeUp?.();
    }
  }, [isPracticeMode, timeLeft, onTimeUp]);

  // Time formatting
  const formattedTime = useMemo(() => {
    if (!timeLeft && timeLeft !== 0) return "";
    const hrs = Math.floor(timeLeft / 3600);
    const mins = Math.floor((timeLeft % 3600) / 60);
    const secs = timeLeft % 60;
    return [hrs, mins, secs].map((t) => String(t).padStart(2, "0")).join(":");
  }, [timeLeft]);

  // Dynamic color based on backend time_status config (test mode only)
  const timerStyle = useMemo(() => {
    if (isPracticeMode) {
      return { backgroundColor: "#F0F3FF", color: "#4F39F6" };
    }

    if (timeLeft === null || timeLeft === undefined || !paperDuration) {
      return { backgroundColor: "#F0F3FF", color: "#4F39F6" };
    }

    const elapsed = Math.max(paperDuration - timeLeft, 0);
    const firstTime = Number(timeStatus.first?.time ?? 0);
    const secondTime = Number(timeStatus.second?.time ?? 0);
    const thirdTime = Number(timeStatus.third?.time ?? 0);
    const hasOutOfRangeThreshold = [firstTime, secondTime, thirdTime]
      .some((t) => Number.isFinite(t) && t > paperDuration + 5);
    const useElapsedMode = !hasOutOfRangeThreshold;

    if (!useElapsedMode) {
      const secondFallback = parseMinutesFromText(timeStatus.second?.toast_text);
      const thirdFallback = parseMinutesFromText(timeStatus.third?.toast_text);
      const secondRemaining = secondTime > 0 && secondTime <= paperDuration
        ? secondTime
        : secondFallback;
      const thirdRemaining = thirdTime > 0 && thirdTime <= paperDuration
        ? thirdTime
        : thirdFallback;

      if (thirdRemaining && timeLeft <= thirdRemaining) {
        return {
          backgroundColor: timeStatus.third?.bg_color || "#FFB5B5",
          color: timeStatus.third?.text_color || "#B70000",
        };
      }

      if (secondRemaining && timeLeft <= secondRemaining) {
        return {
          backgroundColor: timeStatus.second?.bg_color || "#FFE9C0",
          color: timeStatus.second?.text_color || "#8C6400",
        };
      }

      return {
        backgroundColor: timeStatus.first?.bg_color || "#EEF2FF",
        color: timeStatus.first?.text_color || "#4F39F6",
      };
    }

    if (elapsed >= thirdTime && thirdTime > 0) {
      return {
        backgroundColor: timeStatus.third?.bg_color || "#FFB5B5",
        color: timeStatus.third?.text_color || "#B70000",
      };
    }

    if (elapsed >= secondTime && secondTime > 0) {
      return {
        backgroundColor: timeStatus.second?.bg_color || "#FFE9C0",
        color: timeStatus.second?.text_color || "#8C6400",
      };
    }

    if (elapsed >= firstTime || firstTime === 0) {
      return {
        backgroundColor: timeStatus.first?.bg_color || "#EEF2FF",
        color: timeStatus.first?.text_color || "#4F39F6",
      };
    }

    return { backgroundColor: "#F0F3FF", color: "#4F39F6" };
  }, [isPracticeMode, timeLeft, paperDuration, timeStatus]);

  return (
    <div className="timer-container">
      <div className="timer-box" style={timerStyle}>
        {/* <p>{timeLeft > 0 ? formattedTime : "00:00:00"}</p> */}
        <p>{formattedTime}</p>
      </div>

      {toast && (
        <div className="toast-box" style={toast}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default Timer;
